import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Star, MapPin, Phone, Mail, X, Check, Calendar, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { LeadPopup } from "../components/LeadPopup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// NBC Logo component
const NBCLogo = () => (
  <img 
    src="/nbc_logo.jpg"
    alt="Neighborhood Boxing Club"
    className="h-12 md:h-16 object-contain"
  />
);

// ── Weekly Schedule Data (edit times here) ───────────────────────────────────
const SCHEDULE_DATA = [
  { day: "Mon", classes: [
    { time: "6:00 AM",  type: "ron_k"    },
    { time: "10:00 AM", type: "group"    },
    { time: "4:30 PM",  type: "group"    },
    { time: "5:45 PM",  type: "group"    },
    { time: "7:00 PM",  type: "ron_k"    },
  ]},
  { day: "Tue", classes: [
    { time: "10:00 AM", type: "group"    },
    { time: "4:30 PM",  type: "group"    },
    { time: "5:45 PM",  type: "group"    },
    { time: "7:00 PM",  type: "group"    },
  ]},
  { day: "Wed", classes: [
    { time: "6:00 AM",  type: "ron_k"    },
    { time: "10:00 AM", type: "group"    },
    { time: "4:30 PM",  type: "group"    },
    { time: "5:45 PM",  type: "group"    },
  ]},
  { day: "Thu", classes: [
    { time: "10:00 AM", type: "group"    },
    { time: "4:30 PM",  type: "group"    },
    { time: "5:45 PM",  type: "group"    },
    { time: "7:00 PM",  type: "group"    },
  ]},
  { day: "Fri", classes: [
    { time: "6:00 AM",  type: "ron_k"    },
    { time: "10:00 AM", type: "group"    },
    { time: "4:30 PM",  type: "group"    },
    { time: "5:45 PM",  type: "group"    },
  ]},
  { day: "Sat", classes: [
    { time: "10:00 AM",  type: "group"     },
    { time: "11:15 AM",  type: "group"     },
    { time: "12:30 PM",  type: "beginners" },
  ]},
  { day: "Sun", classes: [
    { time: "10:00 AM",  type: "group"    },
    { time: "11:15 AM",  type: "group"    },
  ]},
];

// Returns 0=Mon … 6=Sun based on America/New_York current time
const getTodayET = () => {
  try {
    const d = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
    const js = d.getDay(); // 0=Sun … 6=Sat
    return js === 0 ? 6 : js - 1; // convert to 0=Mon … 6=Sun
  } catch { return 0; }
};

const ClassPill = ({ cls, onBook }) => {
  if (cls.type === "ron_k") {
    return (
      <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,182,18,0.55)", borderRadius: 20, padding: "4px 8px", textAlign: "center" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>{cls.time}</div>
        <div style={{ fontSize: 9, fontWeight: 800, color: "#FFB612", lineHeight: 1.3 }}>Workout With Ron K</div>
        <div style={{ fontSize: 9, color: "rgba(255,182,18,0.6)" }}>14 max</div>
      </div>
    );
  }
  if (cls.type === "beginners") {
    return (
      <div style={{ border: "1px solid rgba(255,182,18,0.55)", borderRadius: 8, padding: "5px 6px", textAlign: "center", background: "rgba(255,255,255,0.03)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, marginBottom: 2 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#fff" }}>{cls.time}</span>
          <span style={{ border: "1px solid rgba(255,182,18,0.7)", color: "#FFB612", fontSize: 8, fontWeight: 900, borderRadius: 10, padding: "0px 5px", background: "transparent" }}>FREE</span>
        </div>
        <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.7)", marginBottom: 3 }}>FREE Beginners Class</div>
        <button
          onClick={onBook}
          style={{ background: "none", border: "none", color: "#FFB612", fontSize: 9, fontWeight: 800, padding: 0, cursor: "pointer", textDecoration: "underline" }}
        >
          Book free class
        </button>
      </div>
    );
  }
  // type === "group"
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "3px 7px", textAlign: "center" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>{cls.time}</div>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.9)" }}>Group Boxing Class</div>
    </div>
  );
};

const WeeklySchedule = ({ onBook }) => {
  const todayIdx = getTodayET();
  const [activeDay, setActiveDay] = useState(todayIdx);

  return (
    <section style={{ background: "#0A0A0A", padding: "0 0 32px" }}>
      {/* Slim booking notice — no solid fill */}
      <div
        onClick={onBook}
        style={{ background: "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(255,182,18,0.4)", cursor: "pointer", padding: "10px 20px", display: "flex", alignItems: "center", gap: 8 }}
      >
        <Lock style={{ width: 13, height: 13, color: "#FFB612", flexShrink: 0 }} />
        <span style={{ fontSize: 12, color: "#fff" }}>
          Online booking required. All classes must be booked in advance.
        </span>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#FFB612", fontWeight: 700, whiteSpace: "nowrap" }}>Book online →</span>
      </div>

      <div style={{ maxWidth: 1152, margin: "0 auto", padding: "24px 16px 0" }}>
        {/* Desktop: 7-column grid */}
        <div className="hidden md:grid" style={{ gridTemplateColumns: "repeat(7, 1fr)", gap: 8 }}>
          {SCHEDULE_DATA.map((d, i) => (
            <div
              key={d.day}
              style={{
                background: i === todayIdx ? "rgba(255,182,18,0.04)" : "transparent",
                border: i === todayIdx ? "1px solid rgba(255,182,18,0.25)" : "1px solid rgba(255,255,255,0.05)",
                borderRadius: 10, padding: "10px 6px",
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 900, textTransform: "uppercase", textAlign: "center", marginBottom: 8, color: i === todayIdx ? "#FFB612" : "rgba(255,255,255,0.85)", letterSpacing: "0.1em" }}>
                {d.day}{i === todayIdx ? " · Today" : ""}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {d.classes.map((cls, j) => <ClassPill key={j} cls={cls} onBook={onBook} />)}
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: swipeable day tabs */}
        <div className="md:hidden">
          <div style={{ display: "flex", overflowX: "auto", gap: 6, paddingBottom: 10, scrollbarWidth: "none" }}>
            {SCHEDULE_DATA.map((d, i) => (
              <button
                key={d.day}
                onClick={() => setActiveDay(i)}
                style={{
                  flexShrink: 0, padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 800,
                  textTransform: "uppercase", cursor: "pointer",
                  background: "transparent",
                  border: activeDay === i ? "1px solid rgba(255,182,18,0.7)" : "1px solid rgba(255,255,255,0.08)",
                  color: activeDay === i ? "#FFB612" : (i === todayIdx ? "rgba(255,182,18,0.8)" : "rgba(255,255,255,0.85)"),
                  borderBottom: activeDay === i ? "2px solid #FFB612" : "1px solid rgba(255,255,255,0.08)",
                }}
              >
                {d.day}{i === todayIdx ? " ·" : ""}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: 320 }}>
            {SCHEDULE_DATA[activeDay].classes.map((cls, j) => (
              <ClassPill key={j} cls={cls} onBook={onBook} />
            ))}
          </div>
        </div>

        {/* Notes */}
        <p style={{ textAlign: "center", color: "rgba(255,255,255,0.75)", fontSize: 11, marginTop: 14 }}>
          Class times are the same every week; coaches may rotate.
        </p>
        <p style={{ textAlign: "center", color: "rgba(255,255,255,0.75)", fontSize: 11, marginTop: 4 }}>
          Free loaner gloves · Hand wraps required ($9.99) · Family Plan available · Parking available · No contract
        </p>
      </div>
    </section>
  );
};

// ── Mission & About Carousel ──────────────────────────────────────────────────
const MISSION_SLIDES = [
  {
    label: "Our Mission",
    title: "Built For Everyday People",
    body: "At Neighborhood Boxing Club, our mission is simple: help everyday people get stronger, healthier, and more confident through the power of boxing. This place was built on the belief that real training creates real results, and that anyone—no matter their starting point—can transform themselves with the right environment and support."
  },
  {
    label: "About Us",
    title: "Neighborhood Boxing Club",
    body: "Neighborhood Boxing Club was founded in 2018 with one goal: create a place where everyday people can experience the power of boxing without intimidation. Our classes combine real boxing training with a supportive environment that helps members get stronger, leaner, and more confident."
  },
  {
    label: "Beginner Friendly",
    title: "No Experience Needed",
    body: "Never thrown a punch before? Perfect. Our classes are designed so anyone can start, learn the basics, and build confidence from day one."
  },
  {
    label: "Real Results",
    title: "Burn Up To 1,000 Calories Per Workout",
    body: "Our high-energy heavy bag workouts combine real boxing training with conditioning designed to help you lose weight, build endurance, and get stronger fast."
  },
  {
    label: "No Fighting",
    title: "Real Boxing Training — No Contact",
    body: "Train like a real boxer on heavy bags while improving stamina, coordination, and power. No sparring. No fighting. Just an incredible workout."
  },
  {
    label: "Community",
    title: "More Than a Gym",
    body: "Neighborhood Boxing Club is a supportive community where members push each other to become stronger, healthier, and more confident."
  },
  {
    label: "Free First Class",
    title: "Your First Class Is Free",
    body: "Come experience the workout, learn the basics, and see why so many people fall in love with boxing fitness."
  }
];

const MissionCarousel = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % MISSION_SLIDES.length);
    }, 10000);
    return () => clearInterval(timer);
  }, []);

  const prev = () => setCurrent(c => (c - 1 + MISSION_SLIDES.length) % MISSION_SLIDES.length);
  const next = () => setCurrent(c => (c + 1) % MISSION_SLIDES.length);

  return (
    <section className="py-16 px-4 md:px-8 bg-[#0A0A0A] border-t border-b border-white/5">
      <div className="max-w-3xl mx-auto">
        <div className="relative">
          {/* All slides in DOM — inactive ones hidden with CSS only, never removed */}
          <div className="relative min-h-[220px]">
            {MISSION_SLIDES.map((slide, i) => (
              <div
                key={i}
                className="text-center px-8 md:px-16 min-h-[220px] flex flex-col justify-center"
                style={{
                  position: i === current ? "relative" : "absolute",
                  top: 0, left: 0, width: "100%",
                  opacity: i === current ? 1 : 0,
                  transition: "opacity 0.5s ease",
                  pointerEvents: i === current ? "auto" : "none",
                }}
              >
                <p className="text-xs uppercase tracking-[0.3em] text-[#FFB612] font-bold mb-4">{slide.label}</p>
                {(slide.label === "Our Mission" || slide.label === "About Us") ? (
                  <h2 className="text-2xl md:text-3xl font-black uppercase mb-5 leading-tight">{slide.title}</h2>
                ) : (
                  <div className="text-2xl md:text-3xl font-black uppercase mb-5 leading-tight">{slide.title}</div>
                )}
                <p className="text-white/70 leading-relaxed text-sm md:text-base">{slide.body}</p>
              </div>
            ))}
          </div>

          {/* Arrows */}
          <button
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-white/30 hover:text-[#FFB612] transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-white/30 hover:text-[#FFB612] transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-8">
          {MISSION_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`w-2 h-2 rounded-full transition-all ${current === i ? 'bg-[#FFB612] w-5' : 'bg-white/20'}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export const HomePage = () => {
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [hasClosedPopup, setHasClosedPopup] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [transformations, setTransformations] = useState([]);
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [currentReview, setCurrentReview] = useState(0);
  const [currentTransformation, setCurrentTransformation] = useState(0);
  const [gymInfo, setGymInfo] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [popupSettings, setPopupSettings] = useState(null);
  const popupTimerRef = useRef(null);
  const [faqOpenIdx, setFaqOpenIdx] = useState(null);

  // Checks localStorage/sessionStorage only — no React state dependency
  const shouldShowPopup = () => {
    if (localStorage.getItem("memberToken")) return false;
    if (localStorage.getItem("nbc_popup_completed")) return false;
    if (sessionStorage.getItem("hasSeenPopup")) return false;
    return true;
  };

  useEffect(() => {
    loadData();
    return () => clearTimeout(popupTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const loadData = async () => {
    try {
      const [reviewsRes, transformationsRes, plansRes, gymInfoRes, popupRes] = await Promise.all([
        axios.get(`${API}/homepage-reviews`),
        axios.get(`${API}/transformations/approved`),
        axios.get(`${API}/plans/homepage`),
        axios.get(`${API}/gym-info`),
        axios.get(`${API}/popup-settings`).catch(() => ({ data: null })),
      ]);
      
      setReviews(reviewsRes.data);
      setTransformations(transformationsRes.data);
      setMembershipPlans(plansRes.data);
      setGymInfo(gymInfoRes.data);

      const settings = popupRes.data;
      if (settings) setPopupSettings(settings);

      // Start auto-popup timer ONLY after confirming is_active AND active_offer from DB
      // Never fire during react-snap prerender — popup must not be baked into saved HTML
      const isPrerender = navigator.userAgent === "ReactSnap";
      const popupActive = !isPrerender && settings ? (settings.is_active !== false && !!settings.active_offer) : false;
      if (popupActive && shouldShowPopup()) {
        popupTimerRef.current = setTimeout(() => {
          setShowPopup(true);
        }, 2000);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };
  
  const handleClosePopup = () => {
    setShowPopup(false);
    setHasClosedPopup(true);
    sessionStorage.setItem("hasSeenPopup", "true");
  };

  // Called by LeadPopup when user completes the offer flow (accept or decline)
  const handlePopupCompleted = () => {
    setShowPopup(false);
    sessionStorage.setItem("hasSeenPopup", "true");
    localStorage.setItem("nbc_popup_completed", "true");
  };
  
  const handleBookFirstClassClick = () => {
    if (window.gtag) window.gtag('event', 'click_book_free_class');
    // No active offer or popup is OFF — route directly to register
    if (!popupSettings?.is_active || !popupSettings?.active_offer) {
      navigate("/register?offer=firstclass");
      return;
    }
    setShowPopup(true);
  };
  
  const nextReview = () => {
    setCurrentReview((prev) => (prev + 1) % Math.max(reviews.length, 1));
  };
  
  const prevReview = () => {
    setCurrentReview((prev) => (prev - 1 + reviews.length) % Math.max(reviews.length, 1));
  };
  
  const nextTransformation = () => {
    setCurrentTransformation((prev) => (prev + 1) % Math.max(transformations.length, 1));
  };
  
  const prevTransformation = () => {
    setCurrentTransformation((prev) => (prev - 1 + transformations.length) % Math.max(transformations.length, 1));
  };

  // Auto-advance Reviews every 10 seconds
  useEffect(() => {
    const len = reviews.length > 0 ? reviews.length : 3;
    if (len <= 1) return;
    const t = setInterval(() => setCurrentReview(p => (p + 1) % len), 10000);
    return () => clearInterval(t);
  }, [reviews.length]);

  // Auto-advance Transformations every 10 seconds
  useEffect(() => {
    if (transformations.length <= 1) return;
    const t = setInterval(() => setCurrentTransformation(p => (p + 1) % transformations.length), 10000);
    return () => clearInterval(t);
  }, [transformations.length]);

  // Only real reviews with content — no placeholders ever
  const displayReviews = reviews.filter(r => r.comment && r.comment.trim());

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Lead Capture Popup */}
      <LeadPopup
        isOpen={showPopup}
        onClose={handleClosePopup}
        onComplete={handlePopupCompleted}
        dynamicOffer={popupSettings?.active_offer || null}
        dynamicUpsell={popupSettings?.active_offer?.upsell || null}
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <NBCLogo />

          {/* Centered contact info — always visible between logo and menu */}
          <div className="flex-1 flex flex-col items-center justify-center mx-2">
            <a href="https://maps.google.com/?q=4324+N+High+St+Columbus+OH+43214"
               target="_blank" rel="noreferrer"
               onClick={() => { if(window.gtag) window.gtag('event','click',{link_type:'directions'}); if(window.fbq) window.fbq('track','Contact'); }}
               className="flex items-center gap-1 text-sm font-bold text-white/80 hover:text-white transition-colors leading-tight">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="hidden sm:inline">4324 N High St, Columbus OH</span>
              <span className="sm:hidden">4324 N High St</span>
            </a>
            <a href="tel:+16145037888"
               onClick={() => { if(window.gtag) window.gtag('event','click',{link_type:'phone'}); if(window.fbq) window.fbq('track','Contact'); }}
               className="flex items-center gap-1 text-sm text-white/60 hover:text-[#FFB612] transition-colors leading-tight mt-0.5">
              <Phone className="w-3 h-3 flex-shrink-0" />
              <span>(614) 503-7888</span>
            </a>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#plans" className="text-white/80 hover:text-white transition-colors font-medium">Memberships</a>
            <a href="#transformations" className="text-white/80 hover:text-white transition-colors font-medium">Results</a>
            <a href="#reviews" className="text-white/80 hover:text-white transition-colors font-medium">Reviews</a>
            <Button 
              onClick={() => navigate('/login')}
              variant="ghost"
              className="text-[#FFB612] hover:text-[#FFB612]/80"
            >
              Member Login
            </Button>
            <Button 
              onClick={handleBookFirstClassClick}
              className="bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase tracking-wide"
            >
              Book First Class Free
            </Button>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
            </svg>
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0A0A0A] border-t border-white/10 py-4 px-4 animate-fade-in">
            <div className="flex flex-col gap-4">
              <a href="#plans" className="text-white/80 py-2">Memberships</a>
              <a href="#transformations" className="text-white/80 py-2">Results</a>
              <a href="#reviews" className="text-white/80 py-2">Reviews</a>
              <Button 
                onClick={() => navigate('/login')}
                variant="outline"
                className="border-white/20 text-white"
              >
                Member Login
              </Button>
              <Button 
                onClick={handleBookFirstClassClick}
                className="bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold"
              >
                Book First Class Free
              </Button>
            </div>
          </div>
        )}
      </nav>
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0">
          <iframe
            src="https://www.youtube.com/embed/Et-bcYVyZ40?autoplay=1&mute=1&loop=1&playlist=Et-bcYVyZ40&controls=0&showinfo=0&rel=0&modestbranding=1"
            title="NBC Boxing"
            className="absolute top-1/2 left-1/2 w-[177.77vh] h-[56.25vw] min-w-full min-h-full -translate-x-1/2 -translate-y-1/2"
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#050505]" />
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black uppercase tracking-tighter mb-6 animate-fade-in-up text-center">
            <span className="block">Boxing For</span>
            <span className="block text-[#FFB612]">Everyone</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/80 mb-8 animate-fade-in-up stagger-1 font-medium">
            Book Your First Class Free
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center animate-fade-in-up stagger-2">
            <Button 
              onClick={handleBookFirstClassClick}
              className="bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase tracking-wide animate-pulse-gold"
              data-testid="hero-claim-pass-btn"
            >
              Book First Class Free
            </Button>
            <Button
              onClick={handleBookFirstClassClick}
              variant="outline"
              className="border-[#FFB612] text-[#FFB612] hover:bg-[#FFB612]/10 font-bold uppercase tracking-wide"
              data-testid="hero-saturday-btn"
            >
              Book Free Beginner Class
            </Button>
            <Button 
              onClick={() => navigate('/login')}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 font-bold uppercase tracking-wide"
              data-testid="hero-book-class-btn"
            >
              Member Login
            </Button>
          </div>
          <p className="text-xs text-white/60 mt-6 flex items-center justify-center gap-1.5 animate-fade-in-up stagger-2">
            <Lock className="w-3 h-3" /> Online booking required — no walk-ins
          </p>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronLeft className="w-8 h-8 text-white/50 rotate-[-90deg]" />
        </div>
      </section>
      
      {/* Weekly Schedule — directly below hero */}
      <WeeklySchedule onBook={handleBookFirstClassClick} />

      {/* Google Reviews Carousel — hidden when no real reviews exist */}
      {displayReviews.length > 0 && (
      <section id="reviews" className="py-24 px-4 md:px-8 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold uppercase text-center mb-4">
            What Our <span className="text-[#FFB612]">Members</span> Say
          </h2>
          {/* 4.9★ Google badge */}
          <div className="flex justify-center mb-8">
            <a
              href="https://www.google.com/maps/place/?q=place_id:ChIJTUsupNiNOIgRdZmBwx7AlBI"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 border border-white/10 px-4 py-2 hover:border-white/30 transition-colors"
            >
              <Star className="w-4 h-4 text-[#FFB612] fill-[#FFB612]" />
              <span className="text-white font-bold text-sm">4.9</span>
              <span className="text-white/50 text-xs">on Google · 303+ reviews</span>
            </a>
          </div>
          
          <div className="relative">
            <div className="overflow-hidden">
              <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${currentReview * 100}%)` }}>
                {displayReviews.map((review, index) => (
                  <div key={review.id || index} className="w-full flex-shrink-0 px-4">
                    <div className="bg-[#1A1A1A] border border-white/10 p-8 md:p-12 text-center max-w-2xl mx-auto">
                      <div className="flex justify-center gap-1 mb-6">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`w-6 h-6 ${i < review.rating ? 'text-[#FFB612] fill-[#FFB612]' : 'text-white/20'}`} 
                          />
                        ))}
                      </div>
                      <p className="text-lg md:text-xl text-white/90 mb-6 italic">"{review.comment}"</p>
                      <p className="text-[#FFB612] font-bold uppercase tracking-wide">{review.reviewer_name}</p>
                      {review.relative_time && (
                        <p className="text-white/60 text-sm mt-1">{review.relative_time} · Google</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {displayReviews.length > 1 && (
              <>
                <button 
                  onClick={prevReview}
                  className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-white/50 hover:text-white transition-colors"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button 
                  onClick={nextReview}
                  className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-white/50 hover:text-white transition-colors"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}
            
            {/* Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {displayReviews.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentReview(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${currentReview === index ? 'bg-[#FFB612]' : 'bg-white/20'}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Mission & About Carousel */}
      <MissionCarousel />

      {/* Before/After Transformations */}
      {transformations.length > 0 && (
        <section id="transformations" className="py-24 px-4 md:px-8 bg-[#050505]">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold uppercase text-center mb-4">
              Real <span className="text-[#FFB612]">Results</span>
            </h2>
            <p className="text-center text-white/60 mb-12">Member transformations that inspire</p>
            
            <div className="relative">
              <div className="overflow-hidden">
                <div className="flex transition-transform duration-500" style={{ transform: `translateX(-${currentTransformation * 100}%)` }}>
                  {transformations.map((t, index) => (
                    <div key={t.id} className="w-full flex-shrink-0 px-4">
                      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        <div className="relative">
                          <img 
                            src={t.before_image} 
                            alt="Before" 
                            className="w-full aspect-[3/4] object-cover grayscale"
                          />
                          <div className="absolute bottom-4 left-4 bg-black/80 px-4 py-2 text-sm font-bold uppercase">Before</div>
                        </div>
                        <div className="relative">
                          <img 
                            src={t.after_image} 
                            alt="After" 
                            className="w-full aspect-[3/4] object-cover"
                          />
                          <div className="absolute bottom-4 left-4 bg-[#FFB612] text-black px-4 py-2 text-sm font-bold uppercase">After</div>
                        </div>
                      </div>
                      <div className="text-center mt-8 max-w-2xl mx-auto">
                        <p className="text-lg text-white/80 italic">"{t.story}"</p>
                        <p className="text-[#FFB612] font-bold mt-4">- {t.member_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {transformations.length > 1 && (
                <>
                  <button 
                    onClick={prevTransformation}
                    className="absolute left-0 top-1/2 -translate-y-1/2 p-2 text-white/50 hover:text-white"
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </button>
                  <button 
                    onClick={nextTransformation}
                    className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-white/50 hover:text-white"
                  >
                    <ChevronRight className="w-8 h-8" />
                  </button>
                </>
              )}
            </div>
          </div>
        </section>
      )}
      
      {/* Membership Plans */}
      <section id="plans" className="py-24 px-4 md:px-8 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold uppercase text-center mb-4">
            Choose Your <span className="text-[#FFB612]">Plan</span>
          </h2>
          <p className="text-center text-white/60 mb-12">Start your boxing journey today</p>
          
          {/* Single Class Option */}
          <div className="bg-[#1A1A1A] border border-white/10 p-6 mb-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold uppercase">Single Class</h3>
              <p className="text-white/60">No commitment - just drop in and train</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <span className="text-3xl font-black text-[#FFB612] font-mono">$19.99</span>
                <span className="text-white/60 ml-1">per class</span>
              </div>
              <Button 
                onClick={handleBookFirstClassClick}
                className="bg-white/10 text-white hover:bg-white/20 font-bold uppercase"
              >
                Book Now
              </Button>
            </div>
          </div>
          
          <p className="text-center text-white/40 text-sm mb-8">— OR SAVE WITH A MEMBERSHIP —</p>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {(() => {
              const filtered = membershipPlans.filter(p => !['starter','performance'].includes(p.id));
              const eliteIdx = filtered.findIndex(p => p.name === 'Elite Unlimited' || p.id === 'elite');
              return filtered.flatMap((plan, index) => {
                const planCard = (
                  <div 
                    key={plan.id}
                    className={`relative p-8 border transition-all duration-300 ${
                      plan.name === 'Elite Unlimited' 
                        ? 'bg-[#FFB612]/10 border-[#FFB612] scale-105' 
                        : 'bg-[#1A1A1A] border-white/10 hover:border-[#FFB612]/50'
                    }`}
                  >
                    {plan.name === 'Elite Unlimited' && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#FFB612] text-black px-4 py-1 text-sm font-bold uppercase">
                        Most Popular
                      </div>
                    )}
                    {plan.id === 'family_plan' && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-white text-black px-4 py-1 text-sm font-bold uppercase">
                        Family Plan
                      </div>
                    )}
                    
                    <h3 className="text-2xl font-bold uppercase mb-2">{plan.name}</h3>
                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="text-4xl font-black text-[#FFB612] font-mono">${plan.price}</span>
                      <span className="text-white/60">/month</span>
                    </div>
                    
                    <ul className="space-y-3 mb-8">
                      {plan.id === 'family_plan' ? (
                        <>
                          <li className="flex items-center gap-3">
                            <Check className="w-5 h-5 text-[#FFB612]" />
                            <span>Covers your immediate family: parents plus up to 3 kids. Larger families may be approved case by case, so just ask.</span>
                          </li>
                          <li className="flex items-center gap-3">
                            <Check className="w-5 h-5 text-[#FFB612]" />
                            <span>Unlimited classes for everyone</span>
                          </li>
                          <li className="flex items-center gap-3">
                            <Check className="w-5 h-5 text-[#FFB612]" />
                            <span>Month-to-month, upgrade or downgrade anytime</span>
                          </li>
                        </>
                      ) : (
                        <>
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-[#FFB612]" />
                        <span>{plan.classes_per_month === -1 ? 'Unlimited classes' : `${plan.classes_per_month} classes per month`}</span>
                      </li>
                      {plan.additional_class_price > 0 && (
                        <li className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-[#FFB612]" />
                          <span>${plan.additional_class_price} per additional class</span>
                        </li>
                      )}
                      {plan.classes_per_month === -1 && (
                        <li className="flex items-center gap-3">
                          <Check className="w-5 h-5 text-[#FFB612]" />
                          <span>Best value for serious training</span>
                        </li>
                      )}
                      <li className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-[#FFB612]" />
                        <span>Month-to-month, upgrade or downgrade anytime</span>
                      </li>
                        </>
                      )}
                    </ul>
                    
                    <Button 
                      onClick={() => navigate(`/register?plan=${plan.id}`)}
                      className={`w-full font-bold uppercase tracking-wide ${
                        plan.name === 'Elite Unlimited' || plan.id === 'family_plan'
                          ? 'bg-[#FFB612] text-black hover:bg-[#E5A00D]'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                      data-testid={`plan-${plan.id}-btn`}
                    >
                      Get Started
                    </Button>
                  </div>
                );

                // Inject diet plan card immediately after Elite Unlimited
                if (index === eliteIdx) {
                  const dietCard = (
                    // Outer gradient-border wrapper — overflow:visible so starburst sits above the card edge
                    <div
                      key="diet-plan-card"
                      style={{
                        position: 'relative',
                        overflow: 'visible',
                        background: 'linear-gradient(135deg,#FFD84A 0%,#FDB913 35%,#3ddc68 100%)',
                        borderRadius: 22,
                        padding: 2,
                        boxShadow: '0 0 26px rgba(253,185,19,.45),0 0 60px rgba(61,220,104,.18),0 14px 30px rgba(0,0,0,.6)',
                      }}
                    >
                      {/* Scoped CSS — button press only, unique class name, cannot match anything else */}
                      <style>{`.nbc-dp24-btn:active{transform:translateY(3px)!important;box-shadow:0 3px 0 #b97d00,0 6px 12px rgba(253,185,19,.25)!important}`}</style>

                      {/* NEW! starburst — pointer-events:none; clicks pass through to the <a> below */}
                      <div style={{
                        position:'absolute',top:-24,right:10,zIndex:10,
                        pointerEvents:'none',
                        transform:'rotate(12deg)',
                        filter:'drop-shadow(0 4px 8px rgba(0,0,0,.55))',
                        width:74,height:74,
                      }}>
                        <svg width="74" height="74" viewBox="0 0 74 74" xmlns="http://www.w3.org/2000/svg">
                          <polygon
                            points="37,3 42.7,15.7 54,7.6 52.6,21.4 66.4,20 58.3,31.3 71,37 58.3,42.7 66.4,54 52.6,52.6 54,66.4 42.7,58.3 37,71 31.3,58.3 20,66.4 21.4,52.6 7.6,54 15.7,42.7 3,37 15.7,31.3 7.6,20 21.4,21.4 20,7.6 31.3,15.7"
                            fill="#3ddc68" stroke="#07210f" strokeWidth="3"
                          />
                          <text x="37" y="46" textAnchor="middle" fill="#111"
                            style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,fontSize:24}}>
                            NEW!
                          </text>
                        </svg>
                      </div>

                      {/* Inner clickable card — dark bg, clips photo to rounded corners */}
                      <a
                        href="/custom-diet-plan"
                        data-testid="homepage-diet-plan-card"
                        style={{
                          display:'block',background:'#181818',borderRadius:20,
                          overflow:'hidden',textDecoration:'none',color:'inherit',cursor:'pointer',
                        }}
                      >
                        {/* Header photo with fade overlay */}
                        <div style={{position:'relative',height:150,overflow:'hidden'}}>
                          <img
                            src="/diet-plan-card-hero.jpg"
                            alt="Boxing gloves and a healthy meal prep box"
                            loading="lazy"
                            style={{width:'100%',height:'100%',objectFit:'cover',objectPosition:'center',display:'block'}}
                          />
                          <div style={{
                            position:'absolute',inset:0,
                            background:'linear-gradient(180deg,transparent 0%,transparent 55%,rgba(24,24,24,.45) 78%,rgba(24,24,24,.82) 92%,#181818 100%)',
                          }} />
                          <div style={{
                            position:'absolute',top:14,left:16,
                            background:'#111',color:'#FDB913',
                            border:'1.5px solid #FDB913',borderRadius:999,
                            fontSize:11,fontWeight:800,
                            fontFamily:"'Barlow Condensed',sans-serif",
                            letterSpacing:'1.2px',padding:'5px 11px',
                            textTransform:'uppercase',whiteSpace:'nowrap',
                          }}>
                            ADD-ON FOR MEMBERS
                          </div>
                        </div>

                        {/* Body — pulls up over the photo fade */}
                        <div style={{marginTop:-26,padding:'0 20px 22px',position:'relative',zIndex:1}}>

                          {/* Title */}
                          <div style={{
                            fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,
                            fontSize:38,lineHeight:0.95,textTransform:'uppercase',marginBottom:12,
                          }}>
                            <span style={{color:'#ffffff'}}>CUSTOM </span>
                            <span style={{color:'#FDB913',textShadow:'0 0 18px rgba(253,185,19,.4)'}}>DIET PLAN</span>
                          </div>

                          {/* Price row */}
                          <div style={{display:'flex',alignItems:'flex-start',gap:12,marginBottom:12}}>
                            <div style={{
                              fontFamily:"'Barlow Condensed',sans-serif",fontWeight:900,
                              fontSize:64,lineHeight:1,color:'#FDB913',
                              textShadow:'0 0 22px rgba(253,185,19,.45)',
                            }}>
                              <sup style={{fontSize:30,verticalAlign:'super',lineHeight:1}}>$</sup>9.99
                            </div>
                            <div style={{display:'flex',flexDirection:'column',gap:5,paddingTop:6}}>
                              <span style={{
                                background:'#3ddc68',color:'#07210f',borderRadius:999,
                                fontSize:12,fontWeight:800,
                                fontFamily:"'Barlow Condensed',sans-serif",
                                letterSpacing:'1px',padding:'3px 10px',
                                textTransform:'uppercase',display:'inline-block',whiteSpace:'nowrap',
                              }}>MEMBERS</span>
                              <span style={{fontSize:14,color:'#dddddd',fontWeight:600,fontFamily:'Manrope,sans-serif'}}>per month</span>
                            </div>
                          </div>

                          {/* Dashed chip */}
                          <div style={{
                            background:'#262626',border:'1px dashed #555',borderRadius:10,
                            padding:'6px 12px',fontSize:13.5,marginBottom:16,
                            display:'inline-block',fontFamily:'Manrope,sans-serif',
                          }}>
                            <span style={{color:'#cfcfcf'}}>Without membership: </span>
                            <span style={{color:'#ffffff',fontWeight:700}}>$19.99/mo</span>
                          </div>

                          {/* 4 Bullets */}
                          <div style={{display:'flex',flexDirection:'column',gap:11,marginBottom:22}}>
                            {[
                              {emoji:'🥗',text:'A meal plan built around your goals and your favorite foods',gold:true},
                              {emoji:'🛒',text:'Weekly grocery list and a weekly cheat meal made from your favorites',gold:false},
                              {emoji:'💬',text:'Text check-ins from the gym',gold:true},
                              {emoji:'🥊',text:'With your active membership · Cancel anytime',gold:false},
                            ].map(({emoji,text,gold},i)=>(
                              <div key={i} style={{display:'flex',alignItems:'flex-start',gap:10}}>
                                <div style={{
                                  width:34,height:34,borderRadius:11,flexShrink:0,
                                  display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,
                                  background:gold?'rgba(253,185,19,.14)':'rgba(61,220,104,.14)',
                                  border:`1px solid ${gold?'rgba(253,185,19,.4)':'rgba(61,220,104,.45)'}`,
                                }}>{emoji}</div>
                                <span style={{
                                  fontSize:15.5,lineHeight:1.3,color:'#f2f2f2',
                                  fontFamily:'Manrope,sans-serif',paddingTop:2,
                                }}>{text}</span>
                              </div>
                            ))}
                          </div>

                          {/* CTA button — <div> inside <a> (valid HTML; outer <a> handles navigation) */}
                          <div
                            className="nbc-dp24-btn"
                            style={{
                              display:'flex',alignItems:'center',justifyContent:'center',
                              width:'100%',height:58,borderRadius:14,
                              background:'linear-gradient(180deg,#FFE066 0%,#FDB913 55%,#F59E0B 100%)',
                              color:'#111111',
                              fontFamily:"'Barlow Condensed',sans-serif",
                              fontWeight:900,fontSize:18,letterSpacing:'.8px',
                              textTransform:'uppercase',
                              boxShadow:'0 6px 0 #b97d00,0 12px 24px rgba(253,185,19,.35)',
                              transition:'transform .1s,box-shadow .1s',
                              boxSizing:'border-box',gap:8,
                            }}
                          >
                            SEE THE DIET PLAN <span>→</span>
                          </div>

                        </div>
                      </a>
                    </div>
                  );
                  return [planCard, dietCard];
                }
                return [planCard];
              });
            })()}
          </div>

          <p className="text-center text-white/60 text-sm mt-8">Month-to-month plans. No contract.</p>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-24 px-4 md:px-8 bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0 grid-border opacity-30" />
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-bold uppercase mb-6">
            Ready to <span className="text-[#FFB612]">Train</span>?
          </h2>
          <p className="text-xl text-white/70 mb-10">
            Join the NBC family today and start your transformation
          </p>
          <Button 
            onClick={handleBookFirstClassClick}
            className="bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase tracking-wide text-lg px-12 py-6"
            data-testid="cta-get-pass-btn"
          >
            Book Your First Class Free
          </Button>
        </div>
      </section>
      
      {/* FAQ — above footer, answers always in DOM for Google */}
      <section className="py-16 px-4 md:px-8 bg-[#050505] border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold uppercase mb-8 text-center">
            Frequently Asked <span className="text-[#FFB612]">Questions</span>
          </h2>
          <dl className="space-y-1">
            {[
              { q: "Do I need to book before coming in?", a: "Yes. All classes must be booked online in advance. Create your account at neighborhoodboxingclub.us and reserve your spot before you arrive. Walk-ins without a booking can't be admitted." },
              { q: "What's the best boxing gym in Columbus for beginners?", a: "Neighborhood Boxing Club on N High St. Your first class is free, there's a free Saturday beginners class, coaches teach technique from scratch, and we're rated 4.9★ on Google." },
              { q: "Are there boxing classes near Ohio State?", a: "Yes. NBC is at 4324 N High St in Clintonville, just north of campus straight up High Street." },
              { q: "What's the schedule?", a: "Classes run 7 days a week. Weekdays have classes at 10:00 AM, 4:30 PM and 5:45 PM, plus 7:00 PM Mon/Tue/Thu. \"Workout With Ron K\" runs at 6:00 AM Mon/Wed/Fri and 7:00 PM Monday. Weekends have classes at 10:00 AM and 11:15 AM, plus the FREE Beginners Class Saturday at 12:30 PM. Times are the same every week." },
              { q: "How much do classes cost?", a: "Drop-in is $19.99. Elite Unlimited is $79.99/month, and the Family Plan is $139.99/month. Your first class is free." },
              { q: "What's included in the Family Plan?", a: "For $139.99/month, the Family Plan covers your immediate family: parents plus up to 3 kids. Larger families may be approved case by case, so just ask." },
              { q: "Do I need my own gear?", a: "You don't need your own gloves, because we lend free gloves for class and sell them when you're ready. You do need hand wraps. We sell them at the gym for $9.99 plus tax." },
              { q: "Do you offer boxing for kids?", a: "Group classes are for ages 13+. Kids under 13 train one-on-one through private training. Text Ron at (614) 547-6096." },
              { q: "Is there a contract?", a: "No. All plans are month to month with no contract." },
              { q: "Is there parking?", a: "Yes, parking is available at the gym." },
            ].map((item, i) => (
              <div key={i} className="border-b border-white/8">
                <dt>
                  <button
                    onClick={() => setFaqOpenIdx(faqOpenIdx === i ? null : i)}
                    className="w-full text-left py-4 flex items-center justify-between gap-4 text-white font-medium text-sm hover:text-[#FFB612] transition-colors"
                    aria-expanded={faqOpenIdx === i}
                  >
                    <span>{item.q}</span>
                    <span className="text-[#FFB612] text-lg flex-shrink-0">{faqOpenIdx === i ? '−' : '+'}</span>
                  </button>
                </dt>
                <dd
                  style={{ maxHeight: faqOpenIdx === i ? 400 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease' }}
                  className="text-white text-sm pb-4 pr-8"
                >
                  {item.a}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <NBCLogo />
              <p className="font-bold text-white mt-3 text-sm">Neighborhood Boxing Club</p>
              <p className="text-white/60 mt-1 text-sm">
                Train like a champion at Columbus's premier boxing gym.
              </p>
            </div>
            <div>
              <h4 className="font-bold uppercase text-lg mb-4">Location</h4>
              <div className="flex items-start gap-2 text-white/70">
                <MapPin className="w-5 h-5 text-[#FFB612] flex-shrink-0 mt-1" />
                <a href="https://maps.google.com/?q=4324+N+High+St+Columbus+OH+43214"
                   target="_blank" rel="noreferrer"
                   onClick={() => { if(window.gtag) window.gtag('event','click',{link_type:'directions'}); if(window.fbq) window.fbq('track','Contact'); }}
                   className="hover:text-white transition-colors">
                  4324 N High St, Columbus, OH 43214
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-bold uppercase text-lg mb-4">Contact</h4>
              <div className="flex items-center gap-2 text-white/70">
                <Phone className="w-5 h-5 text-[#FFB612]" />
                <a href="tel:+16145037888"
                   onClick={() => { if(window.gtag) window.gtag('event','click',{link_type:'phone'}); if(window.fbq) window.fbq('track','Contact'); }}
                   className="hover:text-white transition-colors">
                  (614) 503-7888
                </a>
              </div>
              <div className="flex items-center gap-2 text-white/70 mt-2">
                <Mail className="w-5 h-5 text-[#FFB612]" />
                <a href="mailto:ronk@neighborhoodboxingclub.com" className="hover:text-white transition-colors">
                  ronk@neighborhoodboxingclub.com
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} Neighborhood Boxing Club. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
